import interstellarApi from './interstellarApi';

export type PlaybackAudioType = 'greeting' | 'announce' | 'hold_music' | 'message' | 'ivr_prompt';

export interface PlaybackAudio {
  id: number;
  account_id: number;
  name: string;
  playback_type: PlaybackAudioType;
  file_path: string;
  public_url: string;
  created_at?: string | null;
  modified_at?: string | null;
}

interface PlaybackAudiosListResponse {
  total_count: number;
  page: number;
  page_size: number;
  playback_audios: PlaybackAudio[];
}

interface CreatePlaybackAudioResponse {
  message?: string;
  id: number;
  name?: string;
  playback_type?: string;
  public_url?: string;
}

/**
 * Lista audios de reproducción de la cuenta: `GET /incoming-call-config/playback-audio`
 */
export const fetchPlaybackAudios = async (
  accountId: number,
  pageSize = 200,
  playbackType?: PlaybackAudioType
): Promise<PlaybackAudio[]> => {
  const params: Record<string, unknown> = {
    account_id: accountId,
    page: 1,
    page_size: pageSize,
  };
  if (playbackType) {
    params.playback_type = playbackType;
  }
  const response = await interstellarApi.get<PlaybackAudiosListResponse>(
    '/incoming-call-config/playback-audio',
    { params }
  );
  return response.data.playback_audios ?? [];
};

/**
 * Obtiene detalle de un audio: `GET /incoming-call-config/playback-audio/{playbackId}`
 */
export const fetchPlaybackAudio = async (playbackId: number): Promise<PlaybackAudio> => {
  const response = await interstellarApi.get<PlaybackAudio>(
    `/incoming-call-config/playback-audio/${playbackId}`
  );
  return response.data;
};

/**
 * Sube un nuevo audio de reproducción: `POST /incoming-call-config/playback-audio`
 * Usa multipart/form-data para el archivo.
 */
export const createPlaybackAudio = async (
  accountId: number,
  name: string,
  playbackType: PlaybackAudioType,
  file: File
): Promise<CreatePlaybackAudioResponse> => {
  const formData = new FormData();
  formData.append('file', file);

  const response = await interstellarApi.post<CreatePlaybackAudioResponse>(
    '/incoming-call-config/playback-audio',
    formData,
    {
      params: {
        account_id: accountId,
        name,
        playback_type: playbackType,
      },
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    }
  );
  return response.data;
};

/**
 * Actualiza metadata de un audio: `PUT /incoming-call-config/playback-audio/{playbackId}`
 */
export const updatePlaybackAudio = async (
  playbackId: number,
  payload: { name?: string }
): Promise<{ message: string; id: number }> => {
  const response = await interstellarApi.put<{ message: string; id: number }>(
    `/incoming-call-config/playback-audio/${playbackId}`,
    payload
  );
  return response.data;
};

/**
 * Elimina un audio: `DELETE /incoming-call-config/playback-audio/{playbackId}`
 */
export const deletePlaybackAudio = async (playbackId: number): Promise<void> => {
  await interstellarApi.delete(`/incoming-call-config/playback-audio/${playbackId}`);
};

/**
 * Importa un audio desde una URL externa (ej. Cosmos S3).
 * Descarga el archivo y lo sube a S3 de Interstellar.
 * `POST /incoming-call-config/playback-audio/from-url`
 */
export const importPlaybackFromUrl = async (
  accountId: number,
  name: string,
  playbackType: PlaybackAudioType,
  sourceUrl: string
): Promise<CreatePlaybackAudioResponse> => {
  const response = await interstellarApi.post<CreatePlaybackAudioResponse>(
    '/incoming-call-config/playback-audio/from-url',
    {
      name,
      playback_type: playbackType,
      source_url: sourceUrl,
    },
    {
      params: { account_id: accountId },
    }
  );
  return response.data;
};
